/* eslint-disable camelcase */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { user } from '../../../redux/user';
import { managementCompany } from '../../../redux/managementCompany';
import {
	useGetManagementNotificationsSettingsQuery,
	useUpdateManagementNotificationsSettingsMutation
} from '../../../redux/managementCompanySettings';
import { PERMISSION } from '../../../constants';
import utils from '../../../utils/utils';
import { showNotification } from '../../../redux/notification';
import IsAuthorizedForManagementCompany from '../../../components/IsAuthorizedForManagementCompany/IsAuthorizedForManagementCompany';
import infoIcon from '../../../assets/images/info-icon-gray.svg';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import MultiOptionsSelector from './MultiOptionsSelector';

import './NotificationsSettings.scss';

const NotificationsSettings = () => {
	const dispatch = useDispatch();
	const { managementCompanyId } = useParams();
	const userData = useSelector(user, shallowEqual) || {};
	const managementCompanyData = useSelector(managementCompany, shallowEqual);

	const [allowCredentialNotifications, setAllowCredentialNotifications] = useState(false);
	const [credentialNotificationsIntervals, setCredentialNotificationsIntervals] = useState([]);
	const [employeeStatuses, setEmployeeStatuses] = useState([]);

	const hasNotificationsEditPermission =
		userData.managementCompanyPermissions &&
		utils.isAuthorizedExact(
			PERMISSION.SETTINGS_NOTIFICATIONS_EDIT,
			userData.managementCompanyPermissions[managementCompanyId]
		);

	const { data: notificationSettingsOptions, isLoading } = useGetManagementNotificationsSettingsQuery(
		managementCompanyId,
		{
			refetchOnMountOrArgChange: true
		}
	);
	const [updateNotificationsSetting, { isLoading: isSaving }] = useUpdateManagementNotificationsSettingsMutation();

	const initNotificationSettings = () => {
		setAllowCredentialNotifications(notificationSettingsOptions.allow_credential_notifications);
		let defaultState = notificationSettingsOptions.intervals.filter((option) => option.is_enabled).length === 0;
		setCredentialNotificationsIntervals(
			notificationSettingsOptions.intervals.map((item) => ({
				label: item.name,
				value: item.code,
				checked: item.is_enabled || defaultState
			}))
		);
		defaultState = notificationSettingsOptions.statuses.filter((option) => option.is_enabled).length === 0;
		setEmployeeStatuses(
			notificationSettingsOptions.statuses.map((item) => ({
				label: item.name.charAt(0).toUpperCase() + item.name.slice(1),
				value: item.code,
				checked: item.is_enabled || (defaultState && item.code === 'ENABLED')
			}))
		);
	};

	useEffect(() => {
		if (notificationSettingsOptions) {
			initNotificationSettings();
		}
	}, [notificationSettingsOptions]);

	const updateNotificationIntervals = (options) => setCredentialNotificationsIntervals(options);
	const updateEmployeeStatuses = (options) => setEmployeeStatuses(options);

	const hasChanges =
		!isLoading &&
		(allowCredentialNotifications !== notificationSettingsOptions?.allow_credential_notifications ||
			(allowCredentialNotifications &&
				(credentialNotificationsIntervals?.some(
					(item, idx) => item.checked !== notificationSettingsOptions?.intervals[idx].is_enabled
				) ||
					employeeStatuses?.some(
						(item, idx) => item.checked !== notificationSettingsOptions?.statuses[idx].is_enabled
					))));

	const save = async () => {
		try {
			await updateNotificationsSetting({
				managementCompanyId,
				body: {
					allow_credential_notifications: allowCredentialNotifications,
					intervals: allowCredentialNotifications
						? credentialNotificationsIntervals.map((item) => ({ code: item.value, is_enabled: item.checked }))
						: [],
					statuses: allowCredentialNotifications
						? employeeStatuses.map((item) => ({ code: item.value, is_enabled: item.checked }))
						: []
				}
			}).unwrap();
			dispatch(showNotification('success', `The notification settings were successfully updated.`));
		} catch (err) {
			dispatch(showNotification('error', `There was an issue saving the changes. Please try again.`));
		}
	};

	const noIntervalSelected = credentialNotificationsIntervals?.filter((option) => option.checked).length === 0;
	const noStatusSelected = employeeStatuses?.filter((option) => option.checked).length === 0;

	return (
		<div className="notifications-settings">
			<div className="header">Notifications - {managementCompanyData.name}</div>
			<div className="content">
				<div className="heading">Notifications</div>
				<div className="row">
					<div className="checkbox-wrapper">
						{hasNotificationsEditPermission ? (
							<input
								type="checkbox"
								id="credentials-expiring-checkbox"
								className="credentials-expiring-checkbox"
								onChange={(e) => setAllowCredentialNotifications(e.target.checked)}
								checked={allowCredentialNotifications}
							/>
						) : (
							<OverlayTrigger
								trigger={['hover', 'focus']}
								placement="bottom"
								overlay={
									<Tooltip className="notifications-settings-tooltip checkbox-tooltip">
										You don’t have permission to edit Notifications.
									</Tooltip>
								}
							>
								<input
									type="checkbox"
									id="credentials-expiring-checkbox"
									className="credentials-expiring-checkbox deactivated"
									onChange={() => {}}
									checked={allowCredentialNotifications}
								/>
							</OverlayTrigger>
						)}
						<label htmlFor="credentials-expiring-checkbox">Credentials - expiring</label>
						<OverlayTrigger
							trigger={['hover', 'focus']}
							placement="bottom"
							overlay={
								<Tooltip className="notifications-settings-tooltip info-tooltip">
									Summary notification for expiring credentials sent to flex professionals when their credentials are
									expiring on timed intervals.
								</Tooltip>
							}
						>
							<img src={infoIcon} alt="Info" className="info-icon" />
						</OverlayTrigger>
					</div>
				</div>
				<div className="row">
					{allowCredentialNotifications && (
						<div className="selectors-box">
							<MultiOptionsSelector
								checkboxOptions={credentialNotificationsIntervals}
								onChange={updateNotificationIntervals}
								readOnly={!hasNotificationsEditPermission}
								fieldLabel="time frames"
							/>
							<MultiOptionsSelector
								checkboxOptions={employeeStatuses}
								onChange={updateEmployeeStatuses}
								readOnly={!hasNotificationsEditPermission}
								fieldLabel="employee statuses"
							/>
						</div>
					)}
				</div>
			</div>
			<IsAuthorizedForManagementCompany
				exactPermission={PERMISSION.SETTINGS_NOTIFICATIONS_EDIT}
				managementCompanyId={managementCompanyId}
			>
				<div className="footer">
					<button type="button" className="btn-light cancel-button" onClick={initNotificationSettings}>
						Cancel
					</button>
					<button
						type="button"
						className="btn-purple save-button"
						onClick={save}
						disabled={
							isLoading || !hasChanges || (allowCredentialNotifications && (noIntervalSelected || noStatusSelected))
						}
					>
						Save
					</button>
				</div>
			</IsAuthorizedForManagementCompany>

			{(isLoading || isSaving) && (
				<div className="spinner-wrapper">
					<LoadingSpinner key="loading-spinner" />
				</div>
			)}
		</div>
	);
};

export default NotificationsSettings;
